/** The browser's own IANA time zone, or `UTC` where the runtime names
 *  none. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Every zone the runtime knows, UTC first; the browser's zone is always
 *  included. */
export function timeZoneOptions(): string[] {
  const known: string[] =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  const zones = new Set(["UTC", browserTimeZone(), ...known]);
  return [...zones];
}

/** The everyday names `timeZoneName` has looked up, by zone: a zone picker
 *  names every zone the runtime knows at each keystroke. */
const TIME_ZONE_NAMES = new Map<string, string>();

/**
 * A time zone's everyday name, for text a person reads: "Pacific Time" for
 * `America/Los_Angeles`, "India Standard Time" for `Asia/Kolkata`, "UTC" for
 * `UTC`. A zone with no such name is named by its offset from GMT
 * ("GMT-05:00").
 */
export function timeZoneName(timeZone: string): string {
  if (timeZone === "UTC" || timeZone === "Etc/UTC") {
    return "UTC";
  }
  let name = TIME_ZONE_NAMES.get(timeZone);
  if (name === undefined) {
    name =
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "longGeneric",
      })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value ?? timeZone;
    TIME_ZONE_NAMES.set(timeZone, name);
  }
  return name;
}

/**
 * A time zone as a zone picker offers it: the city that names the zone and
 * the zone's everyday name, "Los Angeles (Pacific Time)", so two zones that
 * share a name stay apart. UTC, and a zone named by no city, is its
 * everyday name alone.
 */
export function timeZoneChoiceLabel(timeZone: string): string {
  const name = timeZoneName(timeZone);
  const city = timeZone.includes("/")
    ? timeZone.slice(timeZone.lastIndexOf("/") + 1).replaceAll("_", " ")
    : undefined;
  return city === undefined || timeZone.startsWith("Etc/")
    ? name
    : `${city} (${name})`;
}
